import { Autocomplete, TextField, Chip, Checkbox, InputAdornment } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { WidgetProps } from "@rjsf/utils";
import { inputStyles, renderLabel, renderDescriptionTooltip } from "./common";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Shared helpers with SelectWidget to tolerate resolved schemas without enumOptions populated by RJSF
const resolveRef = (ref: string | undefined, rootSchema: any): any => {
  if (!ref || !rootSchema?.definitions) return null;
  const refPath = ref.replace("#/definitions/", "");
  return rootSchema.definitions[refPath];
};

const extractEnumOptions = (s: any): Array<{ value: any; label: string }> => {
  if (!s) return [];
  const options: Array<{ value: any; label: string }> = [];

  if (Array.isArray(s.enum)) {
    const enumNames = s.enumNames;
    s.enum.forEach((val: any, idx: number) => {
      options.push({ value: val, label: enumNames?.[idx] || (val === null ? "None" : String(val)) });
    });
  }

  if (Array.isArray(s.oneOf)) {
    s.oneOf.forEach((opt: any) => {
      if (opt.const !== undefined) {
        options.push({ value: opt.const, label: opt.title || (opt.const === null ? "None" : String(opt.const)) });
      } else if (Array.isArray(opt.enum)) {
        const enumNames = opt.enumNames;
        opt.enum.forEach((val: any, idx: number) => {
          options.push({ value: val, label: enumNames?.[idx] || (val === null ? "None" : String(val)) });
        });
      }
    });
  }

  if (Array.isArray(s.anyOf)) {
    s.anyOf.forEach((opt: any) => {
      if (opt.const !== undefined) {
        options.push({ value: opt.const, label: opt.title || (opt.const === null ? "None" : String(opt.const)) });
      } else if (Array.isArray(opt.enum)) {
        const enumNames = opt.enumNames;
        opt.enum.forEach((val: any, idx: number) => {
          options.push({ value: val, label: enumNames?.[idx] || (val === null ? "None" : String(val)) });
        });
      }
    });
  }

  return options;
};

const findOptions = (s: any, rootSchema: any): Array<{ value: any; label: string }> => {
  const direct = extractEnumOptions(s);
  if (direct.length) return direct;

  if (s?.$ref) {
    const resolved = resolveRef(s.$ref, rootSchema);
    if (resolved) return findOptions(resolved, rootSchema);
  }

  if (s?.type === "array" && s.items) {
    return findOptions(s.items, rootSchema);
  }

  let collected: Array<{ value: any; label: string }> = [];
  if (Array.isArray(s?.oneOf)) {
    s.oneOf.forEach((opt: any) => {
      collected = collected.concat(findOptions(opt, rootSchema));
    });
  }
  if (Array.isArray(s?.anyOf)) {
    s.anyOf.forEach((opt: any) => {
      collected = collected.concat(findOptions(opt, rootSchema));
    });
  }

  return collected;
};

export const TagSelectWidget = (props: WidgetProps) => {
  const { id, value, label, onChange, required, disabled, readonly, options, schema, registry } = props;
  const rootSchema = registry?.rootSchema;

  let enumOptions = options?.enumOptions || [];
  if (!enumOptions.length) {
    // Fallback for resolved schemas (e.g., json-refs output) where RJSF doesn't inject enumOptions
    enumOptions = findOptions(schema, rootSchema);

    // If still empty and the field name exists as a definition/property, try there
    if (!enumOptions.length && rootSchema) {
      const lastIdSegment = id.split("_").pop();
      const candidate = lastIdSegment ? rootSchema?.definitions?.[lastIdSegment] || rootSchema?.properties?.[lastIdSegment] : null;
      if (candidate) {
        enumOptions = findOptions(candidate, rootSchema);
      }
    }
  }
  
  // Value for multiple select is an array
  const selectedValues = Array.isArray(value) ? value : [];

  // Map selected values to option objects
  const selectedOptions = enumOptions.filter((opt: any) => selectedValues.includes(opt.value));

  return (
    <Autocomplete
      multiple
      id={id}
      fullWidth
      options={enumOptions}
      disableCloseOnSelect
      getOptionLabel={(option: any) => option.label}
      isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
      value={selectedOptions}
      onChange={(_event, newValue) => {
        onChange(newValue.map((v: any) => v.value));
      }}
      disabled={disabled || readonly}
      limitTags={5}
      slotProps={{
        popper: {
            sx: {
                // Ensure dropdown is wide enough even if input is narrow
                minWidth: "300px !important",
                zIndex: 1300
            }
        }
      }}
      renderOption={(props, option, { selected }) => {
        // Extract key from props to avoid React warning, pass the rest
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={option.label}
              {...tagProps}
              size="small"
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={renderLabel(label, !!required)}
          placeholder={selectedValues.length === 0 ? "Select..." : ""}
          sx={inputStyles}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {schema.description && (
                    <InputAdornment position="end" sx={{ ml: 1 }}>
                        {renderDescriptionTooltip(schema.description)}
                    </InputAdornment>
                )}
              </>
            ),
          }}
        />
      )}
    />
  );
};
